/**
 * Resolves the signed-in user's company info from their email domain.
 *
 * Two-step Apollo query chain:
 *   1. `searchCompaniesV2(query: <domain>)` → top match's `hgId`.
 *   2. `company(hgId)` → name, description, industry, employee/revenue ranges,
 *      HQ country.
 *
 * Used by `useCopilotContext` behind the `maCopilotContextEnrichment` LD flag.
 * Returns `undefined` when:
 *   - The flag is off.
 *   - No logged-in profile or no email domain.
 *   - The domain is a free-mail provider (won't identify a company).
 *   - `searchCompaniesV2` returns no match (a `console.debug` is emitted so
 *     coverage gaps surface in dev).
 */
import { gql, useQuery } from "@apollo/client";
import { useMemo } from "react";

import { SEARCH_COMPANY_QUERY } from "../apis/companySearch/searchCompany";
import { getLoggedUserProfile } from "../utils/auth";
import { COMPANY_SEARCH_CLIENT } from "../utils/consts";

// Free-mail providers that won't identify a company. Kept inline; if/when a
// canonical list lives elsewhere (e.g. signup validation), lift to a shared
// utility. Four strings doesn't earn its own file yet.
const FREE_MAIL_DOMAINS = new Set([
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
]);

const SIGNED_IN_USER_COMPANY_QUERY = gql`
  query signedInUserCompany($hgId: Identifier!) {
    company(hgId: $hgId) {
      data {
        hgId
        name
        url
        description
        industry {
          name
        }
        employeeRange {
          min
          max
          band
        }
        revenueRange {
          min
          max
          band
        }
        locationPrimary {
          country {
            name
            alpha2
          }
        }
      }
    }
  }
`;

interface SearchCompaniesResponse {
  searchCompaniesV2: {
    data: Array<{ hgId: string; name: string; url: string | null }> | null;
  } | null;
}

interface SignedInUserCompanyResponse {
  company: {
    data: {
      hgId: string;
      name: string;
      url: string | null;
      description: string | null;
      industry: { name: string | null } | null;
      employeeRange: {
        min: number | null;
        max: number | null;
        band: string | null;
      } | null;
      revenueRange: {
        min: number | null;
        max: number | null;
        band: string | null;
      } | null;
      locationPrimary: {
        country: { name: string | null; alpha2: string | null } | null;
      } | null;
    } | null;
  } | null;
}

export interface SignedInUserCompany {
  name: string;
  domain?: string;
  description?: string;
  industry?: string;
  employee_range?: { min?: number; max?: number; band?: string };
  revenue_range?: { min?: number; max?: number; band?: string };
  hq_country?: string;
}

function extractDomain(email: string | undefined): string | undefined {
  if (!email) return undefined;
  const at = email.indexOf("@");
  if (at === -1 || at === email.length - 1) return undefined;
  const domain = email
    .slice(at + 1)
    .toLowerCase()
    .trim();
  if (!domain || FREE_MAIL_DOMAINS.has(domain)) return undefined;
  return domain;
}

function toRangePayload(
  range: { min: number | null; max: number | null; band: string | null } | null
): { min?: number; max?: number; band?: string } | undefined {
  if (!range) return undefined;
  const out: { min?: number; max?: number; band?: string } = {};
  if (range.min !== null && range.min !== undefined) out.min = range.min;
  if (range.max !== null && range.max !== undefined) out.max = range.max;
  if (range.band) out.band = range.band;
  return Object.keys(out).length > 0 ? out : undefined;
}

interface UseSignedInUserCompanyResult {
  data: SignedInUserCompany | undefined;
  loading: boolean;
  error: Error | undefined;
}

export function useSignedInUserCompany(
  enabled: boolean
): UseSignedInUserCompanyResult {
  const email = getLoggedUserProfile()?.email;
  const domain = enabled ? extractDomain(email) : undefined;

  const skipSearch = !enabled || !domain;

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
  } = useQuery<SearchCompaniesResponse>(SEARCH_COMPANY_QUERY, {
    variables: { query: domain, per: 1 },
    context: { clientName: COMPANY_SEARCH_CLIENT },
    skip: skipSearch,
    fetchPolicy: "cache-first",
  });

  const topMatch = searchData?.searchCompaniesV2?.data?.[0];
  const hgId = topMatch?.hgId;
  const fallbackName = topMatch?.name;

  // Surface coverage gaps without UX cost — a domain query that returns no
  // match is the dev-time signal that the free-mail skiplist needs a new entry
  // OR that ICP-relevant companies are missing from search.
  if (
    enabled &&
    domain &&
    !searchLoading &&
    !searchError &&
    searchData &&
    !hgId
  ) {
    console.debug(
      `[useSignedInUserCompany] No company match for domain ${domain}; skipping enrichment.`
    );
  }

  const skipDetails = skipSearch || !hgId;

  const {
    data: detailsData,
    loading: detailsLoading,
    error: detailsError,
  } = useQuery<SignedInUserCompanyResponse>(SIGNED_IN_USER_COMPANY_QUERY, {
    variables: { hgId },
    context: { clientName: COMPANY_SEARCH_CLIENT },
    skip: skipDetails,
    fetchPolicy: "cache-first",
  });

  const data = useMemo<SignedInUserCompany | undefined>(() => {
    if (skipSearch) return undefined;
    const company = detailsData?.company?.data;
    if (!company) {
      // Search succeeded but details aren't back yet — defer; once loaded,
      // the useMemo re-runs and we ship the enriched shape.
      return undefined;
    }
    const result: SignedInUserCompany = {
      name: company.name ?? fallbackName ?? domain ?? "",
    };
    if (domain) result.domain = domain;
    if (company.description) result.description = company.description;
    if (company.industry?.name) result.industry = company.industry.name;
    const employee = toRangePayload(company.employeeRange);
    if (employee) result.employee_range = employee;
    const revenue = toRangePayload(company.revenueRange);
    if (revenue) result.revenue_range = revenue;
    if (company.locationPrimary?.country?.alpha2) {
      result.hq_country = company.locationPrimary.country.alpha2;
    } else if (company.locationPrimary?.country?.name) {
      result.hq_country = company.locationPrimary.country.name;
    }
    return result;
  }, [skipSearch, detailsData, fallbackName, domain]);

  const error = searchError ?? detailsError;

  return {
    data,
    loading: searchLoading || detailsLoading,
    error,
  };
}
